import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut } from "lucide-react";
import { adminApi, apiError, getToken, setToken, clearToken } from "@/lib/adminApi";
import { ClickStats } from "@/components/admin/ClickStats";
import { InquiryInbox } from "@/components/admin/InquiryInbox";
import { RetentionPanel } from "@/components/admin/RetentionPanel";
import { BannerEditor } from "@/components/admin/BannerEditor";
import { TestimonialsPanel } from "@/components/admin/TestimonialsPanel";
import { PasscodePanel } from "@/components/admin/PasscodePanel";

const PasscodeGate = ({ onSuccess }) => {
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!passcode) return;
    setLoading(true);
    try {
      const { data } = await adminApi.post("/admin/login", { passcode });
      setToken(data.token);
      onSuccess();
    } catch (err) {
      toast.error(apiError(err, "Incorrect passcode."));
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="bg-cream-ivory border border-[#D9CFB8] p-10 max-w-md">
      <Lock size={22} className="text-[#8a6b2f]" />
      <h1 className="font-serif text-4xl text-navy mt-5">Owner access</h1>
      <p className="text-ink-muted text-sm mt-3">Enter your passcode to view clicks, read inquiries, and manage the site banner.</p>
      <label className="block font-mono text-[10px] uppercase tracking-[0.24em] text-ink mt-8" htmlFor="passcode">Passcode</label>
      <input id="passcode" type="password" autoComplete="current-password" data-testid="admin-passcode-input"
        className="w-full bg-transparent border-b border-[#C9BFA8] focus:border-[#8a6b2f] outline-none py-3 text-navy"
        value={passcode} onChange={(e) => setPasscode(e.target.value)} />
      <button type="submit" disabled={loading} data-testid="admin-login-btn"
        className="mt-8 w-full bg-navy text-parchment px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] border border-gold/30 disabled:opacity-60">
        {loading ? "Checking…" : "Unlock"}
      </button>
    </form>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking

  const logout = useCallback(() => { clearToken(); setAuthed(false); }, []);

  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    adminApi.get("/admin/me").then(() => setAuthed(true)).catch(logout);
  }, [logout]);

  return (
    <section data-testid="admin-page" className="bg-cream pt-36 pb-28 min-h-screen">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
        {authed === null ? null : !authed ? <PasscodeGate onSuccess={() => setAuthed(true)} /> : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-[#8a6b2f] mb-4">Owner dashboard</p>
                <h1 className="font-serif text-5xl text-navy">Oakford Ledger</h1>
              </div>
              <button onClick={logout} data-testid="admin-logout-btn"
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-navy link-underline">
                <LogOut size={14} /> Lock
              </button>
            </div>
            <div className="grid gap-10">
              <ClickStats onAuthError={logout} />
              <InquiryInbox onAuthError={logout} />
              <RetentionPanel onAuthError={logout} />
              <BannerEditor onAuthError={logout} />
              <TestimonialsPanel onAuthError={logout} />
              <PasscodePanel onAuthError={logout} />
            </div>
          </>
        )}
      </div>
    </section>
  );
}
